import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Icons from "../icons";
import Loader from "./Loader";
import ConfirmModal from "./ConfirmModal";
import { GherkinLines, scenarioCount } from "./TestCaseRow";
import AppPageArtifactSteps from "./AppPageArtifactSteps";
import { ChecksBadge, ChecksList } from "./AppPageChecks";
import RunSocketNotice from "./RunSocketNotice";
import { openRunSocket } from "../hooks/useRunSocket";
import { appPageRunPoll } from "../hooks/runPolls";

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api/";

export function visionWsUrl(runId) {
  const base = new URL(API_BASE_URL, window.location.origin);
  const proto = base.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${base.host}/api/vision/ws/${runId}`;
}

/* Each test's state while the run works through the queue. */
const STATE = {
  waiting: { dot: "bg-slate-300", label: "Waiting" },
  generating: { dot: "bg-amber-400 animate-pulse", label: "Generating" },
  done: { dot: "bg-emerald-500", label: "Script ready" },
  failed: { dot: "bg-rose-500", label: "No script" },
};

function StateDot({ state }) {
  const s = STATE[state] || STATE.waiting;
  return <span role="img" aria-label={s.label} title={s.label} className={`mt-1 h-3.5 w-3.5 flex-none rounded-full ${s.dot}`} />;
}

/* What the AppPage QA Agent gave for each test (it writes the script; it does not run it). */
const RESULT_TONE = {
  passed: "bg-emerald-50 text-emerald-700",
  partial: "bg-amber-50 text-amber-800",
  failed: "bg-amber-50 text-amber-800",
  blocked: "bg-rose-50 text-rose-700",
  error: "bg-rose-50 text-rose-700",
  not_run: "bg-slate-100 text-slate-600",
};
const RESULT_LABEL = {
  passed: "Script ready",
  partial: "Whole file kept",
  failed: "No script",
  blocked: "Page did not open",
  error: "Error",
  not_run: "Not run",
};

/* Create Artifact for App Page tests. The AppPage QA Agent writes the
   Playwright script of the selected tests (no model to choose). The run's
   scripts are kept on the server until Save writes them as Artifacts (an
   existing Artifact is replaced and its previous version kept). */
export default function AppPageCreateArtifact({ appPage, tests, userkey, initialKeys, onCancel, onDone }) {
  const active = useMemo(() => (tests || []).filter((t) => t.status === "active"), [tests]);
  /* Preselected: the tests passed in (Review's per-test button), else every
     test that is not automated yet. */
  const [selected, setSelected] = useState(
    () => new Set(initialKeys?.length ? initialKeys : active.filter((t) => !t.automated).map((t) => t.test_key))
  );
  const [open, setOpen] = useState(() => new Set());
  const [confirming, setConfirming] = useState(false);

  const [phase, setPhase] = useState("pick"); // pick | running | review
  const [runId, setRunId] = useState(null);
  const [states, setStates] = useState({});
  const [activity, setActivity] = useState("");
  const [results, setResults] = useState([]);
  const [toSave, setToSave] = useState(() => new Set());
  const [openResult, setOpenResult] = useState(() => new Set());
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const wsRef = useRef(null);
  const finishedRef = useRef(false);
  const [connection, setConnection] = useState("idle");

  const byNo = useMemo(() => Object.fromEntries((tests || []).map((t) => [t.test_no, t])), [tests]);
  const byKey = useMemo(() => Object.fromEntries((tests || []).map((t) => [t.test_key, t])), [tests]);
  const chosen = active.filter((t) => selected.has(t.test_key));
  const replacing = chosen.filter((t) => t.automated);

  const stopStreams = useCallback(() => {
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {
        /* already closed */
      }
      wsRef.current = null;
    }
  }, []);
  useEffect(() => stopStreams, [stopStreams]);

  const showResults = useCallback((list) => {
    const items = Array.isArray(list) ? list : [];
    setResults(items);
    setToSave(new Set(items.filter((r) => (r.steps || []).length).map((r) => r.test_key)));
    setPhase("review");
  }, []);

  /* The run row is the source of truth once the run is over. */
  const loadRun = useCallback(
    async (id) => {
      const res = await fetch(`${API_BASE_URL}app-pages/runs/${encodeURIComponent(id)}`);
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      return body.data;
    },
    []
  );

  const finish = useCallback(
    async (id) => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      stopStreams();
      try {
        const run = await loadRun(id);
        if (run.error && !(run.summary?.results || []).some((r) => (r.steps || []).length)) setError(run.error);
        showResults(run.summary?.results || []);
      } catch (e) {
        setError(`Could not load the run's results: ${e.message}`);
        setPhase("review");
      }
    },
    [loadRun, showResults, stopStreams]
  );

  const handleEvent = useCallback(
    (ev, id) => {
      const testOf = (index) => byNo[index]?.test_key;
      switch (ev.type) {
        case "stage":
          if (ev.message) setActivity(ev.message);
          break;
        case "info":
          if (ev.message) setActivity(ev.message);
          break;
        case "scenario_start": {
          const key = testOf(ev.index);
          if (key) setStates((s) => ({ ...s, [key]: { state: "generating" } }));
          break;
        }
        case "scenario_result": {
          const key = testOf(ev.index);
          const made = (ev.generated_steps || []).filter((s) => s.action).length > 0;
          if (key) setStates((s) => ({ ...s, [key]: { state: made ? "done" : "failed", message: ev.reason } }));
          break;
        }
        case "error":
          if (ev.message) setActivity(ev.message);
          break;
        case "done":
          finish(id);
          break;
        default:
          break;
      }
    },
    [byNo, finish]
  );

  const generate = async () => {
    setConfirming(false);
    setError("");
    finishedRef.current = false;
    setStates(Object.fromEntries(chosen.map((t) => [t.test_key, { state: "waiting" }])));
    setActivity("Starting…");
    setPhase("running");
    try {
      const res = await fetch(`${API_BASE_URL}app-pages/${encodeURIComponent(appPage)}/artifacts/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          test_keys: chosen.map((t) => t.test_key),
          userkey: userkey || null,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || !body?.data?.run_id) throw new Error(body?.detail || `HTTP ${res.status}`);
      const id = body.data.run_id;
      setRunId(id);
      /* A drop reconnects, then polls the run row; the row is read for
         the results once the run has ended either way. */
      wsRef.current = openRunSocket(visionWsUrl(id), {
        onEvent: (ev) => handleEvent(ev, id),
        onStatus: setConnection,
        poll: appPageRunPoll(id),
        pollMs: 4000,
        onEnd: () => {
          setConnection("idle");
          finish(id);
        },
      });
    } catch (e) {
      setError(`Could not start Create Artifact: ${e.message}`);
      setPhase("pick");
    }
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}app-pages/runs/${encodeURIComponent(runId)}/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ test_keys: [...toSave], userkey: userkey || null }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      onDone?.(body.data);
    } catch (e) {
      setError(`Could not save: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const toggle = (setter) => (key) =>
    setter((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const toggleSelected = toggle(setSelected);
  const toggleOpen = toggle(setOpen);
  const toggleSave = toggle(setToSave);
  const toggleOpenResult = toggle(setOpenResult);
  const allSelected = active.length > 0 && chosen.length === active.length;
  // One test selected: "Create Artifact"; none or more: "Create Artifacts".
  const createLabel = chosen.length === 1 ? "Create Artifact" : "Create Artifacts";

  return (
    <div className="flex h-[calc(100vh-8rem)] min-h-0 flex-col">
      <div className="mb-4 flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            {createLabel} - {appPage}
          </h1>
          {/* <p className="mt-1 text-sm text-slate-500">
            App Page <span className="font-semibold text-slate-700">{appPage}</span> · the AppPage QA Agent writes the
            Playwright script of each selected test.
          </p> */}
        </div>
      </div>

      {error && (
        <div role="alert" className="mb-3 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <Icons.Alert className="mt-0.5 h-4 w-4 flex-none" />
          <span className="min-w-0 flex-1 break-words">{error}</span>
        </div>
      )}

      {phase === "pick" && (
        <>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/60 shadow-sm">
            <header className="flex flex-none items-center gap-2 border-b border-slate-200 bg-slate-100/70 px-3 py-2.5">
              <Icons.JiraXrayTest />
              <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">
                Tests to automate {chosen.length}/{active.length}
              </h3>
              <button
                type="button"
                onClick={() => setSelected(allSelected ? new Set() : new Set(active.map((t) => t.test_key)))}
                className="rounded-lg px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200/60"
              >
                {allSelected ? "Clear all" : "Select all"}
              </button>
            </header>
            <ul className="cq-scroll min-h-0 flex-1 space-y-2 overflow-y-auto p-2.5">
              {active.map((t) => {
                const isOpen = open.has(t.test_key);
                return (
                  <li key={t.test_key} className="overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm">
                    <div className="flex flex-wrap items-center gap-2 px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={selected.has(t.test_key)}
                        onChange={() => toggleSelected(t.test_key)}
                        aria-label={`Select ${t.test_key}`}
                        className="h-4 w-4 flex-none accent-blue-600"
                      />
                      <button
                        type="button"
                        onClick={() => toggleOpen(t.test_key)}
                        aria-expanded={isOpen}
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                      >
                        <Icons.ChevronRight
                          className={["h-3.5 w-3.5 flex-none text-slate-400 transition-transform", isOpen ? "rotate-90" : ""].join(" ")}
                        />
                        <span className="text-sm font-medium text-blue-600">{t.test_key}</span>
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800" title={t.title}>
                          {t.title}
                        </span>
                      </button>
                      <span className="text-[0.6875rem] text-slate-400">{scenarioCount(t.steps)} steps</span>
                      {t.drifted && (
                        <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800">steps changed</span>
                      )}
                      <ChecksBadge report={t.verification} />
                      {t.automated && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                          <Icons.Check className="h-3.5 w-3.5" /> Automated
                        </span>
                      )}
                    </div>
                    {isOpen && (
                      <div className="border-t border-slate-100 bg-slate-50/60 px-3 py-2">
                        <GherkinLines steps={t.steps} compact />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* The action bar: the AppPage QA Agent writes the scripts, so there is
              nothing to choose, only Create. */}
          <div className="mt-4 flex-none overflow-hidden rounded-2xl border border-slate-200 bg-card shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 bg-slate-50/70 px-6 py-3">
              <p className="flex min-w-0 items-center gap-2 text-sm text-slate-600">
                <Icons.Spark className="h-4 w-4 flex-none text-blue-600" />
                <span>
                  {chosen.length} test{chosen.length === 1 ? "" : "s"} selected · the AppPage QA Agent writes the scripts
                </span>
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onCancel}
                  className="inline-flex items-center rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-800"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    /* Ask only when a selected test already has an Artifact
                       that generating would replace; otherwise just go. */
                    if (replacing.length) setConfirming(true);
                    else generate();
                  }}
                  disabled={chosen.length === 0}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
                >
                  <Icons.Plus className="h-4 w-4" />
                  {createLabel}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {phase === "running" && (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-card shadow-sm">
          <div className="border-b border-slate-200 px-4 py-3">
            <p className="text-sm font-semibold text-slate-800">Generating {chosen.length} Artifact{chosen.length === 1 ? "" : "s"}…</p>
            <p className="mt-0.5 truncate text-xs text-slate-500" title={activity}>{activity}</p>
            <RunSocketNotice status={connection} />
          </div>
          <ul className="cq-scroll min-h-0 flex-1 space-y-1.5 overflow-y-auto p-3">
            {chosen.map((t) => {
              const s = states[t.test_key] || { state: "waiting" };
              return (
                <li key={t.test_key} className="flex items-start gap-2.5 rounded-lg px-2 py-1.5">
                  <StateDot state={s.state} />
                  <span className="min-w-0 flex-1">
                    <span className="text-[0.8125rem] font-semibold text-slate-800">{t.test_key}</span>{" "}
                    <span className="text-[0.8125rem] text-slate-600">{t.title}</span>
                    {s.message && s.state === "failed" && (
                      <span className="block text-[0.6875rem] text-rose-600">{s.message}</span>
                    )}
                  </span>
                  <span className="flex-none text-[0.6875rem] text-slate-400">{(STATE[s.state] || STATE.waiting).label}</span>
                </li>
              );
            })}
          </ul>
          <p className="flex items-center gap-2 border-t border-slate-200 bg-slate-50/70 px-4 py-2.5 text-xs text-slate-500">
            <Icons.Spinner className="h-3.5 w-3.5 flex-none animate-spin" />
            The AppPage QA Agent writes all the selected scripts in one turn. Finished scripts are kept on the server until you save them.
          </p>
        </div>
      )}

      {phase === "review" && (
        <>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/60 shadow-sm">
            <header className="flex flex-none items-center gap-2 border-b border-slate-200 bg-slate-100/70 px-3 py-2.5">
              <Icons.Script className="h-5 w-5 text-slate-600" />
              <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-800">
                Generated scripts · {results.filter((r) => (r.steps || []).length).length} of {results.length}
              </h3>
            </header>
            <ul className="cq-scroll min-h-0 flex-1 space-y-2 overflow-y-auto p-2.5">
              {results.length === 0 && (
                <li className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-400">No script was produced.</li>
              )}
              {results.map((r) => {
                const test = byKey[r.test_key] || {};
                const steps = r.steps || [];
                const isOpen = openResult.has(r.test_key);
                return (
                  <li key={r.test_key} className="overflow-hidden rounded-xl border border-slate-200 bg-card shadow-sm">
                    <div className="flex flex-wrap items-center gap-2 px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={toSave.has(r.test_key)}
                        disabled={!steps.length}
                        onChange={() => toggleSave(r.test_key)}
                        aria-label={`Save ${r.test_key}`}
                        className="h-4 w-4 flex-none accent-blue-600"
                      />
                      <button
                        type="button"
                        onClick={() => toggleOpenResult(r.test_key)}
                        aria-expanded={isOpen}
                        className="flex min-w-0 flex-1 items-center gap-2 text-left"
                      >
                        <Icons.ChevronRight
                          className={["h-3.5 w-3.5 flex-none text-slate-400 transition-transform", isOpen ? "rotate-90" : ""].join(" ")}
                        />
                        <span className="text-sm font-medium text-blue-600">{r.test_key}</span>
                        <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">{test.title || r.title}</span>
                      </button>
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${RESULT_TONE[r.status] || RESULT_TONE.error}`}>
                        {RESULT_LABEL[r.status] || r.status}
                      </span>
                      <ChecksBadge report={r.verification} what="script" label="Script" />
                      <span className="text-[0.6875rem] text-slate-400">{steps.length} script steps</span>
                    </div>
                    {isOpen && (
                      <div className="space-y-2 border-t border-slate-100 bg-slate-50/60 px-3 py-3">
                        {r.reason && <p className="text-[0.75rem] text-slate-600">{r.reason}</p>}
                        {r.verification && <ChecksList report={r.verification} title="Checks of the Playwright script" />}
                        {steps.length > 0 ? (
                          <AppPageArtifactSteps lines={test.steps || []} steps={steps} />
                        ) : (
                          <p className="text-[0.75rem] italic text-slate-400">No script was produced for this test.</p>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
            <p className="min-w-0 flex-1 text-xs text-slate-500">
              Save writes the ticked scripts as Artifacts. An existing Artifact is replaced; its previous version is kept.
              {results.some((r) => (r.verification?.errors || 0) > 0) &&
                " Some scripts have errors against the page: open them to see where before you save."}
            </p>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || toSave.size === 0}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
            >
              {saving ? <Loader.Inline size={14} /> : <Icons.Check className="h-4 w-4" />}
              Save {toSave.size || ""}
            </button>
          </div>
        </>
      )}

      {confirming && (
        <ConfirmModal
          tone="primary"
          icon={Icons.Spark}
          title="Replace existing Artifacts?"
          message={`${replacing.map((t) => t.test_key).join(", ")} already ${
            replacing.length === 1 ? "has an Artifact" : "have Artifacts"
          }. Saving the new script replaces it and keeps the previous version.`}
          confirmLabel={createLabel}
          busyLabel="Starting…"
          onCancel={() => setConfirming(false)}
          onConfirm={generate}
        />
      )}
    </div>
  );
}
